import React, { useMemo, useState } from 'react';
import { Button, Modal } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, LinkOutlined, EnvironmentOutlined, CalendarOutlined, BankOutlined, TeamOutlined, CloseOutlined } from '@ant-design/icons';

/**
 * Expected shape:
 * node = {
 *   timestamp?: string|number, // optional, unix ms or ISO
 *   data: {
 *     contacts: [
 *       {
 *         name?: { first_name?: string, middle_name?: string, last_name?: string, formatted_name?: string },
 *         phones?: [{ phone: string, type?: string }],
 *         emails?: [{ email: string }],
 *         addresses?: [{ street?: string, city?: string, state?: string, zip?: string, country?: string }],
 *         urls?: [{ url: string }],
 *         org?: { company?: string }
 *       },
 *       ...
 *     ]
 *   }
 * }
 */

const WHATSAPP_GREEN = '#008069';

const ContactMessage = ({ node, nodePreview = false }) => {
  const contacts = node?.data?.contacts || [];
  if (!contacts.length) return null;

  if (contacts.length > 1) {
    return <MultiContactsCard node={node} contacts={contacts} nodePreview={nodePreview} />;
  }
  return <SingleContactCard node={node} contact={contacts[0]} nodePreview={nodePreview} />;
};

// ---------- helpers ----------
const toDisplayName = (name = {}) => {
  if (name.formatted_name) return name.formatted_name;
  const parts = [
    name.first_name,
    name.middle_name && name.middle_name.length ? `${name.middle_name[0]}.` : null,
    name.last_name,
  ].filter(Boolean);
  return (parts.join(' ') || 'Contact').trim();
};

const formatClock = (ts) => {
  if (!ts) return null;
  try {
    const d = typeof ts === 'number' ? new Date(ts) : new Date(String(ts));
    // 12:16 pm (lowercase, per screenshot)
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  } catch {
    return null;
  }
};

const phoneDigits = (s = '') => (s || '').replace(/[^\d+]/g, '');
const formatForPreview = (raw = '') => {
  // Keep simple & neutral; WhatsApp shows raw international. We'll lightly group last 10 if present.
  const only = phoneDigits(raw);
  if (only.length < 10) return raw;
  const tail = only.slice(-10);
  return `${only.slice(0, only.length - 10)} ${tail.slice(0, 3)} ${tail.slice(3, 6)} ${tail.slice(6)}`.trim();
};

// ---------- UI atoms ----------
const AvatarCircle = ({ label }) => (
  <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border border-gray-300">
    {/* Fallback icon if no initial */}
    {label ? (
      <span className="text-[0.813rem] font-medium text-gray-600">{label[0]?.toUpperCase()}</span>
    ) : (
      <UserOutlined className="text-gray-500 text-lg" />
    )}
  </div>
);

const TopRow = ({ title, rightText }) => (
  <div className="flex items-center w-full">
    <div className="flex-1 font-semibold text-[0.813rem] text-gray-900 truncate overflow-hidden">{title}</div>
    {rightText ? (
      <div className="ml-2 text-[0.625rem] text-gray-500 truncate max-w-[40%] overflow-hidden">
        {rightText}
      </div>
    ) : null}
  </div>
);

// ---------- Cards ----------
const ContactDetailsPopup = ({ contact, showDetailsPopup, setShowDetailsPopup, onClose }) => {
  if (!contact) return null;
  
  const { name, phones = [], emails = [], addresses = [], urls = [], org = {}, birthday } = contact;
  const displayName = toDisplayName(name || {});
  
  // Helper function to check if section has data and filter out empty/null values
  const hasPhones = phones && phones.length > 0 && phones.some(p => p.phone && p.phone.trim());
  const hasEmails = emails && emails.length > 0 && emails.some(e => e.email && e.email.trim());
  const hasAddresses = addresses && addresses.length > 0 && addresses.some(addr => 
    (addr.street && addr.street.trim()) || 
    (addr.city && addr.city.trim()) || 
    (addr.state && addr.state.trim()) || 
    (addr.zip && addr.zip.trim()) || 
    (addr.country && addr.country.trim())
  );
  const hasUrls = urls && urls.length > 0 && urls.some(u => u.url && u.url.trim());
  const hasBirthday = birthday && birthday.trim();
  const hasOrg = org && (
    (org.company && org.company.trim()) || 
    (org.title && org.title.trim()) || 
    (org.department && org.department.trim())
  );

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (onClose) {
      setShowDetailsPopup(false);
      // onClose(e);
    }
  };
  
  return (
    <Modal
      open={showDetailsPopup}
      onCancel={handleClose}
      onOk={handleClose}
      footer={null}
      centered
      width={400}
      className="contact-details-modal"
      closeIcon={
        <div 
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
          onClick={handleClose}
        >
          <CloseOutlined className="text-white text-sm" />
        </div>
      }
      maskClosable={true}
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      styles={{
        header: { backgroundColor: '#008069', border: 'none' },
        content: { padding: 0, overflow: 'hidden' }
      }}
    >
      <div className="bg-white rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-[#008069] p-4 text-white text-center">
          <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-white flex items-center justify-center text-2xl text-[#008069]">
            {displayName ? displayName[0].toUpperCase() : <UserOutlined />}
          </div>
          <div className="text-lg font-medium capitalize">{displayName}</div>
        </div>
        
        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {/* Phones */}
          {hasPhones && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">PHONE</div>
              {phones.filter(phone => phone.phone && phone.phone.trim()).map((phone, i) => (
                <div key={i} className="flex items-center py-2 border-b border-gray-100">
                  <PhoneOutlined className="text-gray-500 mr-3" />
                  <div>
                    <div className="text-sm">{formatForPreview(phone.phone)}</div>
                    <div className="text-xs text-gray-500 capitalize">{phone.type?.toLowerCase() || 'mobile'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Emails */}
          {hasEmails && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">EMAIL</div>
              {emails.filter(email => email.email && email.email.trim()).map((email, i) => (
                <div key={i} className="flex items-center py-2 border-b border-gray-100">
                  <MailOutlined className="text-gray-500 mr-3" />
                  <div>
                    <div className="text-sm">{email.email}</div>
                    <div className="text-xs text-gray-500 capitalize">{email.type?.toLowerCase() || 'work'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Addresses */}
          {hasAddresses && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">ADDRESS</div>
              {addresses.filter(addr => 
                (addr.street && addr.street.trim()) || 
                (addr.city && addr.city.trim()) || 
                (addr.state && addr.state.trim()) || 
                (addr.zip && addr.zip.trim()) || 
                (addr.country && addr.country.trim())
              ).map((addr, i) => (
                <div key={i} className="flex items-start py-2 border-b border-gray-100">
                  <EnvironmentOutlined className="text-gray-500 mr-3 mt-1" />
                  <div>
                    <div className="text-sm">
                      {[addr.street, addr.city, addr.state, addr.zip, addr.country].filter(field => field && field.trim()).join(', ')}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">{addr.type?.toLowerCase() || 'work'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Birthday */}
          {hasBirthday && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">BIRTHDAY</div>
              <div className="flex items-center py-2">
                <CalendarOutlined className="text-gray-500 mr-3" />
                <div className="text-sm">{new Date(birthday).toLocaleDateString()}</div>
              </div>
            </div>
          )}
          
          {/* Organization */}
          {hasOrg && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">ORGANIZATION</div>
              <div className="flex items-start py-2">
                <BankOutlined className="text-gray-500 mr-3 mt-1" />
                <div>
                  {org.title && org.title.trim() && <div className="text-sm font-medium">{org.title}</div>}
                  {org.company && org.company.trim() && <div className="text-sm">{org.company}</div>}
                  {org.department && org.department.trim() && <div className="text-xs text-gray-500">{org.department}</div>}
                </div>
              </div>
            </div>
          )}
          
          {/* URLs */}
          {hasUrls && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">WEBSITE</div>
              {urls.filter(url => url.url && url.url.trim()).map((url, i) => (
                <div key={i} className="flex items-center py-2 border-b border-gray-100">
                  <LinkOutlined className="text-gray-500 mr-3" />
                  <a 
                    href={url.url.startsWith('http') ? url.url : `https://${url.url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-[#008069] hover:underline"
                  >
                    {url.url.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const SingleContactCard = ({ node, contact, nodePreview }) => {
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const displayName = toDisplayName(contact?.name || {});
  const phones = contact?.phones || [];
  const hasPhone = phones.some(phone => phone.phone?.trim());
  const hasWhatsApp = phones.some(phone => phone.wa_id);
  const ts = useMemo(() => formatClock(node?.timestamp), [node?.timestamp]);

  const handleCardClick = (e) => {
    e.stopPropagation();
    // Don't trigger if clicking on action buttons or if popup is already open
    if (!e.target.closest('.action-button') && !showDetailsPopup) {
      setShowDetailsPopup(true);
    }
  };

  const handleCloseModal = (e) => {
    if (e) {
      e.stopPropagation();
    }
  };

  return (
    <div 
      className={`w-full rounded-2xl overflow-hidden ${nodePreview ? 'cursor-pointer' : ''}`}
      onClick={nodePreview ? handleCardClick : undefined}
    >
      {/* Header */}
      <div className="px-2 pt-1 pb-3">
        <div className="flex items-center gap-3">
          <AvatarCircle label={displayName} />
          <div className="flex-1">
            <TopRow title={displayName} rightText={ts} />
          </div>
        </div>
      </div>

      {/* Action row */}
      {hasPhone && (
        <div className={`flex items-center justify-around p-1 gap-3 ${nodePreview ? 'min-w-[300px]' : ''}`} style={{borderTop: "1.5px solid rgba(0, 0, 0, 0.1)"}}>
          {hasWhatsApp ? (
            <>
              <div 
                className="action-button flex items-center justify-center text-[rgb(27,135,85)] hover:text-[#1f7a5b] cursor-pointer select-none text-[0.813rem] pt-2"
              >
                Message
              </div>
              <div 
                className="action-button flex items-center justify-center text-[rgb(27,135,85)] hover:text-[#1f7a5b] cursor-pointer select-none text-[0.813rem] pt-2"
              >
                Save contact
              </div>
            </>
          ) : (
            <div 
              className="action-button flex items-center justify-center text-[rgb(27,135,85)] hover:text-[#1f7a5b] cursor-pointer select-none text-[0.813rem] pt-2 w-full"
            >
              Invite
            </div>
          )}
        </div>
      )}
      
      {/* Contact Details Popup */}
      {nodePreview && (
        <ContactDetailsPopup 
          contact={contact}
          showDetailsPopup={showDetailsPopup}
          setShowDetailsPopup={setShowDetailsPopup}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

const MultiContactsCard = ({ node, contacts, nodePreview }) => {
  const first = contacts[0];
  const name = toDisplayName(first?.name || {});
  const others = contacts.length - 1;
  const ts = useMemo(() => formatClock(node?.timestamp), [node?.timestamp]);

  return (
    <div className="w-full rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-2 pt-1 pb-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            {/* Two stacked avatars like the screenshot */}
            <div className="absolute -left-3 top-0">
              <AvatarCircle label={toDisplayName(contacts[1]?.name || {})} />
            </div>
            <div className="relative left-3">
              <AvatarCircle label={name} />
            </div>
          </div>
          <div className="flex-1">
            <TopRow
              title={`${name} and ${others} other contact${others > 1 ? 's' : ''}`}
              rightText={ts}
            />
          </div>
        </div>
      </div>

      {/* View all row */}
      <div className="border-t border-gray-100" />
      <div className="px-2 pt-1">
        <Button
          type="link"
          block
          onClick={() => {}}
          className="!h-9 !text-[0.813rem] !text-[#1f7a5b] !no-underline hover:!underline"
          style={{ color: WHATSAPP_GREEN }}
        >
          View all
        </Button>
      </div>
    </div>
  );
};

export default ContactMessage;